"use client";

import { useMemo, useState, useCallback } from "react";
import { ArrowLeftRight, LoaderCircle, Pencil, Plus, X } from "lucide-react";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import {
  EntityInstance,
  EntitySchema,
  isLocalId,
  newLocalId,
  PaginationData,
  withoutLocalId,
} from "@/services/entity-instance.service";
import { RelationOptionDialog } from "../../relation-option-dialog";
import { Button } from "../../ui/button";
import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { EntityPermissions } from "@/hooks/schema/use-my-permissions";
import { EntityViewComponentMapping } from "@/services/entity-view-components.service";
import { useEntityInstanceModalStack } from "@/stores/entity-instance-modal-stack-store";
import { SelectedItemList } from "./selected-item-list";
import {
  PendingChange,
  PendingChangeBadge,
  pendingChangeStyles,
  ViewComponentRenderer,
} from "../../relation-display-components/view-component-renderer";
import { cn } from "@repo/utils/utils";

export const highlightedStyles = " ring-2 ring-secondary ring-inset ";

export interface OptionType {
  id: string;
  [fieldName: string]: string;
}

export const RelationInstanceSelect = ({
  isSingle,
  options,
  entityName,
  selectedInstance,
  selectedInstances = [],
  onSelect,
  onUnselect,
  selectedOptions,
  idsToDisconnect, // to calculate the correct total number in the connect component
  editedIds,
  value,
  optionSearchStr,
  onOptionSearchChange,
  permissions,
  relationFieldToDisplay,
  mapping,
  isTightlyCoupled,
}: {
  selectedInstance: EntityInstance | null;
  selectedInstances: EntityInstance[];
  options: UseInfiniteQueryResult<
    InfiniteData<
      {
        entityInstances: unknown[];
        pagination: PaginationData;
      },
      unknown
    >,
    Error
  >;
  selectedOptions: UseInfiniteQueryResult<
    InfiniteData<
      {
        data: object | object[] | null;
        pagination?: PaginationData;
      },
      unknown
    >,
    Error
  >;
  entityName: string;
  isSingle?: boolean;
  entitySchema: EntitySchema;
  idsToDisconnect: Set<string>;
  editedIds?: Set<string>;
  onSelect?: (instance: EntityInstance) => void;
  onUnselect?: (id: string) => void;
  value?: OptionType;
  optionSearchStr: string;
  onOptionSearchChange: (search: string) => void;
  permissions: EntityPermissions;
  relationFieldToDisplay?: string;
  mapping?: EntityViewComponentMapping;
  isTightlyCoupled?: boolean;
}) => {
  const { push } = useEntityInstanceModalStack();

  //here we store newly created and selected options to be able to show them in the relation-select view
  const [bufferedOptions, setBufferedOptions] = useState<Array<OptionType>>([]);

  const allOptions = useMemo(() => {
    const _options = (options.data?.pages.flatMap((p) => p.entityInstances) ??
      []) as Array<OptionType>;

    // Build map with query results first
    const optionsMap = new Map(_options.map((x) => [x.id, x]));

    // Add/override with value
    if (value) {
      optionsMap.set(value.id, value);
    }

    selectedOptions.data?.pages
      .flatMap((x) => x.data ?? [])
      .forEach((o) => {
        const opt = o as OptionType;
        optionsMap.set(opt.id, opt);
      });

    // Unsaved nested edits win; merged because they only carry the changed fields.
    bufferedOptions.forEach((opt) => {
      optionsMap.set(opt.id, { ...optionsMap.get(opt.id), ...opt });
    });

    return Array.from(optionsMap.values());
  }, [options, bufferedOptions, value, selectedOptions]);

  const pushCreateOrUpdateSubModalToStack = useCallback(
    (id?: string) => {
      // Find the cached instance from allOptions (includes bufferedOptions)
      const cachedInstance = id ? allOptions.find((opt) => opt.id === id) : undefined;
      // An unsaved create reopens as a create form, prefilled with what was entered so far.
      const isUnsavedCreate = !id || isLocalId(id);
      const localId = isUnsavedCreate ? (id ?? newLocalId()) : undefined;

      push({
        key: `${entityName}-${id}-${crypto.randomUUID()}`,
        entityName,
        entityInstanceId: isUnsavedCreate ? undefined : id,
        entityFromListCache: isUnsavedCreate ? undefined : cachedInstance,
        initialValues:
          isUnsavedCreate && cachedInstance ? withoutLocalId(cachedInstance) : undefined,
        onSave: (data) => {
          const instance = { ...data.data, id: localId ?? data.data.id } as OptionType;
          // push the updated sub item into the parents _connect field so it will be upserted by the parent
          onSelect?.(instance);
          setBufferedOptions((prev) => {
            const existingIndex = prev.findIndex((x) => x.id === instance.id);
            if (existingIndex !== -1) {
              // Merge the updated fields into the existing instance to preserve relations
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                ...instance,
              };
              return updated;
            }
            return [...prev, instance];
          });
          return Promise.resolve();
        },
      });
    },
    [push, entityName, allOptions, onSelect], // Add allOptions to deps
  );

  const { state } = useEntityInstanceLayoutStore(entityName);

  // Use the custom field if specified, otherwise use the default field
  const displayField = relationFieldToDisplay || state.field;

  const pages = (selectedOptions as SelectedOptionsType)?.data?.pages.filter((x) => !!x) ?? [];

  const selectedOptionElements = pages
    .flatMap((page) =>
      Array.isArray(page.data) ? page.data.map((o) => o as OptionType) : [page.data as OptionType],
    )
    .filter((x): x is OptionType => x != null);

  const serverSelectedIds = new Set(selectedOptionElements.map((x) => x.id));
  const selectedIds = new Set(
    (Array.isArray(selectedInstances) ? selectedInstances : []).map((y) => y.id),
  );
  const newlyAddedIds = new Set(Array.from(selectedIds).filter((id) => !serverSelectedIds.has(id)));
  const serverDisconnectedCount = Array.from(idsToDisconnect).filter((id) =>
    serverSelectedIds.has(id),
  ).length;

  const serverTotal =
    pages[pages.length - 1]?.pagination?.total ||
    //TODO: remove the second one. we only do this, because the backend is retuning total = 0 for to many relations on ourself
    pages.reduce((acc, page) => acc + (page?.data?.length ?? 0), 0) ||
    0;

  const totalToDisplay = serverTotal - serverDisconnectedCount + newlyAddedIds.size;

  const pendingChangeOf = (id: string | undefined): PendingChange | undefined => {
    if (!selectedOptions.isSuccess) return undefined;
    if (!id || !serverSelectedIds.has(id)) return "new";
    return editedIds?.has(id) ? "edited" : undefined;
  };
  const singlePendingChange = pendingChangeOf(selectedInstance?.id);

  const selectedSingleOption = useMemo(() => {
    return allOptions.find((x) => x.id === selectedInstance?.id) as OptionType;
  }, [allOptions, selectedInstance]);

  return (
    <div className="relative">
      {!isSingle && (
        <div className="absolute -top-5 right-1 text-xs text-neutral-500">
          {selectedOptions.isFetching ? (
            <span>
              <LoaderCircle className="animate-spin" size={12} />
            </span>
          ) : (
            <span>({totalToDisplay})</span>
          )}
        </div>
      )}

      {/* Single selection - show selected item with edit/remove */}
      {isSingle &&
        selectedInstance &&
        selectedSingleOption &&
        (mapping ? (
          // Custom component handles its own container and buttons
          <ViewComponentRenderer
            mapping={mapping}
            id={selectedInstance.id}
            option={selectedSingleOption}
            entityName={entityName}
            relationFieldToDisplay={relationFieldToDisplay}
            onOptionClick={pushCreateOrUpdateSubModalToStack}
            onDelete={onUnselect}
            pendingChange={singlePendingChange}
          />
        ) : (
          <div
            className={cn(
              "group flex h-10 items-center justify-between gap-1 rounded-md border px-3 hover:bg-accent/40",
              singlePendingChange && pendingChangeStyles,
            )}
          >
            {/* Click the item to open & edit it in the deeper modal */}
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
              title={`Open ${entityName} to edit`}
              onClick={() => pushCreateOrUpdateSubModalToStack(selectedInstance.id)}
            >
              <span className="truncate text-sm">
                {selectedSingleOption?.[displayField] ||
                  (selectedSingleOption?.integer_id
                    ? `Item with numeric id: ${selectedSingleOption.integer_id}`
                    : "")}
              </span>
              <Pencil className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-70" />
            </button>
            {singlePendingChange && <PendingChangeBadge change={singlePendingChange} />}
            <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              {/* Exchange = open the picker to replace with a different one */}
              {!isTightlyCoupled && (
                <RelationOptionDialog
                  name=""
                  options={options}
                  isSingle={true}
                  selectedInstances={selectedInstances}
                  onSelect={(id) => {
                    setBufferedOptions((prev) => [...prev, allOptions.find((x) => x.id === id)!]);
                    onSelect?.({ id });
                  }}
                  onUnselect={onUnselect}
                  entityName={entityName}
                  searchValue={optionSearchStr}
                  setSearchValue={onOptionSearchChange}
                  onCreateClick={
                    permissions.has_create ? pushCreateOrUpdateSubModalToStack : undefined
                  }
                  relationFieldToDisplay={relationFieldToDisplay}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      className="h-7 w-7 flex-shrink-0 p-0 text-muted-foreground"
                      title={`Exchange for another ${entityName}`}
                    >
                      <ArrowLeftRight className="h-4 w-4" />
                    </Button>
                  }
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="h-7 w-7 flex-shrink-0 p-0 text-muted-foreground hover:text-foreground"
                title="Disconnect"
                onClick={() => onUnselect?.(selectedInstance.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

      {/* Single selection - tightly coupled: only allow creating new */}
      {isSingle && !selectedInstance && isTightlyCoupled && permissions.has_create && (
        <Button
          variant="outline"
          type="button"
          className="w-full justify-start font-normal text-muted-foreground"
          onClick={() => pushCreateOrUpdateSubModalToStack()}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create new {entityName}
        </Button>
      )}

      {/* Single selection - connect button when nothing selected (non-tightly-coupled) */}
      {isSingle && !selectedInstance && !isTightlyCoupled && (
        <RelationOptionDialog
          name={`Connect ${entityName}…`}
          options={options}
          isSingle={true}
          selectedInstances={selectedInstances}
          onSelect={(id) => {
            setBufferedOptions((prev) => [...prev, allOptions.find((x) => x.id === id)!]);
            onSelect?.({ id });
          }}
          onUnselect={onUnselect}
          entityName={entityName}
          searchValue={optionSearchStr}
          setSearchValue={onOptionSearchChange}
          onCreateClick={permissions.has_create ? pushCreateOrUpdateSubModalToStack : undefined}
          relationFieldToDisplay={relationFieldToDisplay}
          mapping={mapping}
        />
      )}

      {/* Multi selection - always show connect button */}
      {!isSingle && (
        <RelationOptionDialog
          name={`Connect ${entityName}…`}
          options={options}
          isSingle={false}
          selectedInstances={selectedInstances}
          onSelect={(id) => {
            setBufferedOptions((prev) => [...prev, allOptions.find((x) => x.id === id)!]);
            onSelect?.({ id });
          }}
          onUnselect={onUnselect}
          entityName={entityName}
          searchValue={optionSearchStr}
          setSearchValue={onOptionSearchChange}
          onCreateClick={permissions.has_create ? pushCreateOrUpdateSubModalToStack : undefined}
          relationFieldToDisplay={relationFieldToDisplay}
          isTightlyCoupled={isTightlyCoupled}
        />
      )}

      {/* Multi selection - list of selected items */}
      {!isSingle && totalToDisplay > 0 && (
        <SelectedItemList
          idsToDisconnect={idsToDisconnect}
          selectedOptions={selectedOptions as SelectedOptionsType}
          options={Array.from(selectedInstances.map((x) => x.id)).map(
            (id) => allOptions.find((y) => y.id === id) || ({ id } as OptionType),
          )}
          onDelete={onUnselect}
          onOptionClick={pushCreateOrUpdateSubModalToStack}
          pendingChangeOf={pendingChangeOf}
          entityName={entityName}
          relationFieldToDisplay={relationFieldToDisplay}
          mapping={mapping}
        />
      )}
    </div>
  );
};

export type SelectedOptionsType = UseInfiniteQueryResult<
  InfiniteData<
    {
      data: object[];
      pagination?: PaginationData;
    },
    unknown
  >,
  Error
>;
